import React, { useState, useEffect, useRef } from 'react';
import Layout from './Layout';
import { Radio, Users, Activity, Clock, Eye, Wifi, WifiOff, Play, Square, Film, Megaphone, TrendingUp, Zap, CalendarClock, Tv2, Cpu, HardDrive, MemoryStick, Calendar } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useLanguage } from '../contexts/LanguageContext';
import { useStreamStats } from '../contexts/StreamStatsContext';
import { useTheme } from '../contexts/ThemeContext';

// Mini sparkline using SVG — no extra dependencies
function SparklineSVG({ data, color = '#818cf8', width = 80, height = 28 }) {
  if (!data || data.length < 2) return <div style={{ width, height }} />;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ overflow: 'visible' }}>
      <defs>
        <linearGradient id={`sg-${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon
        points={`0,${height} ${pts} ${width},${height}`}
        fill={`url(#sg-${color.replace('#','')})`}
      />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Dashboard({ user, onLogout }) {
  const { t, timezone } = useLanguage();
  const { sessionStats, isStreaming, resetSessionStats } = useStreamStats();
  const { isDark } = useTheme();
  const gridStroke = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.07)';
  const axisStroke = isDark ? '#9ca3af' : '#6B7280';
  const tooltipStyle = isDark
    ? { background: 'rgba(17,24,39,0.95)', border: '1px solid rgba(129,140,248,0.3)', borderRadius: '12px', backdropFilter: 'blur(12px)', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }
    : { background: 'rgba(255,255,255,0.97)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: '12px', backdropFilter: 'blur(12px)', boxShadow: '0 8px 32px rgba(0,0,0,0.1)' };
  const tooltipLabelStyle = isDark ? { color: '#fff', fontWeight: 'bold' } : { color: '#111827', fontWeight: 'bold' };
  const [stats, setStats] = useState({
    activeChannels: 0,
    totalChannels: 0,
    totalViewers: 0,
    bandwidth: '0 Mbps',
    uptime: '0h 0m'
  });
  const [systemInfo, setSystemInfo] = useState(null);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [nowPlayingLoading, setNowPlayingLoading] = useState(true);
  const [simulcastPlatforms, setSimulcastPlatforms] = useState([]);
  const [simulcastLoading, setSimulcastLoading] = useState(true);
  const [channelOnlineStatus, setChannelOnlineStatus] = useState({});
  const [currentTime, setCurrentTime] = useState('');
  const [systemHistory, setSystemHistory] = useState({ cpu: [], ram: [], disk: [] });
  const systemHistoryRef = useRef({ cpu: [], ram: [], disk: [] });
  const hasLiveChannel = stats.activeChannels > 0 || Object.values(channelOnlineStatus).some(Boolean);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem('token');

        const [channelsRes, realTimeRes] = await Promise.all([
          axios.get('/api/channels?limit=5', {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get('/api/analytics/real-time', {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        setChannels(channelsRes.data.channels || []);

        const realTimeData = realTimeRes.data;
        setStats({
          activeChannels: realTimeData.activeChannels || 0,
          totalChannels: realTimeData.channels?.total || 0,
          totalViewers: realTimeData.totalViewers || 0,
          bandwidth: realTimeData.bandwidth || '0 B/s',
          uptime: realTimeData.uptime || '0h 0m'
        });

        setSystemInfo(realTimeData.system);

        // Accumulate system history for sparklines (keep last 20 readings)
        if (realTimeData.system) {
          const cpuVal = realTimeData.system.cpu?.usage ?? 0;
          const ramVal = realTimeData.system.memory?.usagePercent ?? 0;
          const diskVal = realTimeData.system.disk?.usagePercent ?? 0;
          const h = systemHistoryRef.current;
          h.cpu = [...h.cpu.slice(-19), cpuVal];
          h.ram = [...h.ram.slice(-19), ramVal];
          h.disk = [...h.disk.slice(-19), diskVal];
          systemHistoryRef.current = h;
          setSystemHistory({ cpu: [...h.cpu], ram: [...h.ram], disk: [...h.disk] });
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        if (error.response?.status !== 401) {
          toast.error('Error loading dashboard data');
        }
        setLoading(false);
      }
    };

    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 15000); // 15 seconds
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch Now Playing data
  useEffect(() => {
    const fetchNowPlaying = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/dashboard/now-playing', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setNowPlaying(response.data);
        setNowPlayingLoading(false);
      } catch (error) {
        console.error('Error fetching now playing:', error);
        setNowPlayingLoading(false);
      }
    };

    fetchNowPlaying();
    const interval = setInterval(fetchNowPlaying, 5000); // Update every 5 seconds
    return () => clearInterval(interval);
  }, []);

  // Fetch Simulcast Platforms
  useEffect(() => {
    const fetchSimulcastPlatforms = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/simulcast', {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Asegurarse de que siempre sea un array y obtener el array de simulcasts
        const simulcasts = response.data.simulcasts || [];
        setSimulcastPlatforms(Array.isArray(simulcasts) ? simulcasts : []);
        setSimulcastLoading(false);
      } catch (error) {
        console.error('Error fetching simulcast platforms:', error);
        setSimulcastPlatforms([]); // Set empty array on error
        setSimulcastLoading(false);
      }
    };

    fetchSimulcastPlatforms();
    const interval = setInterval(fetchSimulcastPlatforms, 10000); // Update every 10 seconds
    return () => clearInterval(interval);
  }, []);

  // Poll unified URL for each channel to detect real online/offline status
  useEffect(() => {
    if (channels.length === 0) return;
    const checkChannels = async () => {
      const results = {};
      await Promise.all(
        channels
          .filter(ch => ch.stream_key)
          .map(async (ch) => {
            try {
              const res = await fetch(`/api/stream/${ch.stream_key}/status`, { cache: 'no-store' });
              if (!res.ok) { results[ch.stream_key] = false; return; }
              const data = await res.json();
              results[ch.stream_key] = Boolean(data.online);
            } catch (_) {
              results[ch.stream_key] = false;
            }
          })
      );
      setChannelOnlineStatus(prev => ({ ...prev, ...results }));
    };
    checkChannels();
    const interval = setInterval(checkChannels, 15000);
    return () => clearInterval(interval);
  }, [channels]);

  // Update current time every second using the configured timezone
  useEffect(() => {
    const updateTime = () => {
      try {
        setCurrentTime(
          new Intl.DateTimeFormat('es-ES', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
            timeZone: timezone || 'UTC'
          }).format(new Date())
        );
      } catch (_) {
        // Fallback to UTC if timezone is invalid
        setCurrentTime(new Date().toUTCString().slice(17, 25));
      }
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [timezone]);

  const toggleSimulcast = async (platformId, currentEnabled) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `/api/simulcast/${platformId}`,
        { enabled: !currentEnabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update local state immediately
      setSimulcastPlatforms(platforms =>
        platforms.map(p =>
          p.id === platformId ? { ...p, enabled: !currentEnabled } : p
        )
      );

      toast.success(`Simulcast ${!currentEnabled ? 'activado' : 'desactivado'}`);
    } catch (error) {
      console.error('Error toggling simulcast:', error);
      toast.error('Error al actualizar simulcast');
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex justify-center items-center h-screen">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-accent-purple/30 border-t-accent-purple rounded-full animate-spin"></div>
            <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-accent-indigo rounded-full animate-spin" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-[10px] min-h-screen max-w-[1600px] mx-auto">
        {/* Header with gradient text */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2 text-gray-900 dark:text-white">
            {t('controlPanel')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-base flex items-center gap-2">
            <span className="inline-block w-2 h-2 bg-accent-emerald rounded-full animate-pulse"></span>
            {t('welcomeBack')}, {user?.full_name || user?.email?.split('@')[0]}
          </p>
        </div>

        {/* Premium Stats Grid with glassmorphism */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Channels Card */}
          <div className="stat-card-premium group">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="text-gray-400 text-sm mb-2 font-medium">
                  {t('createdChannels')}
                </div>
                <div className="text-3xl font-bold text-white mb-3 transition-all duration-300 group-hover:text-accent-purple">
                  {stats.totalChannels || 0}
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-purple/10 border border-accent-purple/20">
                    <div className="w-1.5 h-1.5 bg-accent-purple rounded-full animate-pulse"></div>
                    <span className="text-accent-purple text-xs font-semibold">{stats.activeChannels} {t('liveNow')}</span>
                  </div>
                </div>
              </div>
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-accent-purple to-accent-indigo flex items-center justify-center shadow-lg shadow-accent-purple/25 transition-all duration-300 group-hover:shadow-accent-purple/40">
                <Radio size={28} className="text-white" />
              </div>
            </div>
          </div>

          {/* Current Viewers Card */}
          <div className="stat-card-premium group">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="text-gray-400 text-sm mb-2 font-medium">
                  Espectadores
                </div>
                <div className="text-3xl font-bold text-white mb-3 transition-all duration-300 group-hover:text-accent-indigo">
                  {stats.totalViewers || 0}
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-indigo/10 border border-accent-indigo/20">
                    <TrendingUp size={12} className="text-accent-indigo" />
                    <span className="text-accent-indigo text-xs font-semibold">Pico: {sessionStats.peakViewers}</span>
                  </div>
                </div>
              </div>
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-accent-indigo to-blue-500 flex items-center justify-center shadow-lg shadow-accent-indigo/25 transition-all duration-300 group-hover:shadow-accent-indigo/40">
                <Users size={28} className="text-white" />
              </div>
            </div>
          </div>

          {/* Current Bandwidth Card */}
          <div className="stat-card-premium group">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="text-gray-400 text-sm mb-2 font-medium">
                  Ancho de Banda
                </div>
                <div className="text-3xl font-bold text-white mb-3 transition-all duration-300 group-hover:text-accent-emerald">
                  {Math.round(sessionStats.currentBandwidth / 1024 / 1024)} Mbps
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-emerald/10 border border-accent-emerald/20">
                    <Zap size={12} className="text-accent-emerald" />
                    <span className="text-accent-emerald text-xs font-semibold">Pico: {Math.round(sessionStats.peakBandwidth / 1024 / 1024)} Mbps</span>
                  </div>
                </div>
              </div>
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-accent-emerald to-green-500 flex items-center justify-center shadow-lg shadow-accent-emerald/25 transition-all duration-300 group-hover:shadow-accent-emerald/40">
                <Activity size={28} className="text-white" />
              </div>
            </div>
          </div>

          {/* Stream Session Status Card */}
          <div className="stat-card-premium group">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="text-gray-400 text-sm mb-2 font-medium">
                  Streaming
                </div>
                <div className={`text-3xl font-bold mb-3 transition-all duration-300 ${hasLiveChannel ? 'text-status-live' : 'text-gray-500'}`}>
                  {hasLiveChannel ? 'Activa' : 'Inactiva'}
                </div>
                <div className="flex flex-col gap-1.5">
                  <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full w-fit ${
                    hasLiveChannel
                      ? 'bg-status-live/10 border border-status-live/20'
                      : 'bg-gray-500/10 border border-gray-500/20'
                  }`}>
                    <Clock size={12} className={hasLiveChannel ? 'text-status-live' : 'text-gray-500'} />
                    <span className={`text-xs font-semibold tabular-nums ${hasLiveChannel ? 'text-status-live' : 'text-gray-500'}`}>
                      {currentTime}
                    </span>
                  </div>
                </div>
              </div>
              <div className={`w-14 h-14 rounded-xl flex items-center justify-center shadow-lg transition-all duration-300 ${
                hasLiveChannel
                  ? 'bg-gradient-to-br from-status-live to-red-600 shadow-status-live/25 group-hover:shadow-status-live/40'
                  : 'bg-gradient-to-br from-gray-600 to-gray-700 shadow-gray-600/25 group-hover:shadow-gray-600/40'
              }`}>
                {hasLiveChannel ? <Play size={28} className="text-white" /> : <Square size={28} className="text-white" />}
              </div>
            </div>
          </div>
        </div>

        {/* PA-2: System Health + Next Event Row */}
        {systemInfo && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8 items-start">
            {/* System Health: CPU / RAM / Disk */}
            <div className="lg:col-span-8 glass-card">
              <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
                <Cpu size={16} className="text-accent-purple" />
                Salud del Sistema
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* CPU */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <Cpu size={13} className="text-accent-purple" />
                      <span className="text-xs text-gray-400 font-medium">CPU</span>
                    </div>
                    <span className={`text-sm font-bold tabular-nums ${
                      (systemInfo.cpu?.usage ?? 0) > 80 ? 'text-red-400' :
                      (systemInfo.cpu?.usage ?? 0) > 60 ? 'text-amber-400' : 'text-accent-emerald'
                    }`}>{Math.round(systemInfo.cpu?.usage ?? 0)}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden mb-2">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${
                        (systemInfo.cpu?.usage ?? 0) > 80 ? 'bg-red-500' :
                        (systemInfo.cpu?.usage ?? 0) > 60 ? 'bg-amber-400' : 'bg-accent-purple'
                      }`}
                      style={{ width: `${Math.min(100, systemInfo.cpu?.usage ?? 0)}%` }}
                    />
                  </div>
                  <div className="hidden sm:block"><SparklineSVG data={systemHistory.cpu} color="#818cf8" width={100} height={24} /></div>
                  <div className="text-xs text-gray-500 mt-1">{systemInfo.cpu?.cores} núcleos</div>
                </div>
                {/* RAM */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <MemoryStick size={13} className="text-accent-indigo" />
                      <span className="text-xs text-gray-400 font-medium">RAM</span>
                    </div>
                    <span className={`text-sm font-bold tabular-nums ${
                      (systemInfo.memory?.usagePercent ?? 0) > 85 ? 'text-red-400' :
                      (systemInfo.memory?.usagePercent ?? 0) > 70 ? 'text-amber-400' : 'text-accent-emerald'
                    }`}>{Math.round(systemInfo.memory?.usagePercent ?? 0)}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden mb-2">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${
                        (systemInfo.memory?.usagePercent ?? 0) > 85 ? 'bg-red-500' :
                        (systemInfo.memory?.usagePercent ?? 0) > 70 ? 'bg-amber-400' : 'bg-accent-indigo'
                      }`}
                      style={{ width: `${Math.min(100, systemInfo.memory?.usagePercent ?? 0)}%` }}
                    />
                  </div>
                  <div className="hidden sm:block"><SparklineSVG data={systemHistory.ram} color="#6366f1" width={100} height={24} /></div>
                  <div className="text-xs text-gray-500 mt-1">{systemInfo.memory?.used} / {systemInfo.memory?.total}</div>
                </div>
                {/* Disk */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5">
                      <HardDrive size={13} className="text-accent-emerald" />
                      <span className="text-xs text-gray-400 font-medium">Disco</span>
                    </div>
                    <span className={`text-sm font-bold tabular-nums ${
                      (systemInfo.disk?.usagePercent ?? 0) > 90 ? 'text-red-400' :
                      (systemInfo.disk?.usagePercent ?? 0) > 75 ? 'text-amber-400' : 'text-accent-emerald'
                    }`}>{Math.round(systemInfo.disk?.usagePercent ?? 0)}%</span>
                  </div>
                  <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden mb-2">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${
                        (systemInfo.disk?.usagePercent ?? 0) > 90 ? 'bg-red-500' :
                        (systemInfo.disk?.usagePercent ?? 0) > 75 ? 'bg-amber-400' : 'bg-accent-emerald'
                      }`}
                      style={{ width: `${Math.min(100, systemInfo.disk?.usagePercent ?? 0)}%` }}
                    />
                  </div>
                  <div className="hidden sm:block"><SparklineSVG data={systemHistory.disk} color="#34d399" width={100} height={24} /></div>
                  <div className="text-xs text-gray-500 mt-1">{systemInfo.disk?.used} / {systemInfo.disk?.total}</div>
                </div>
              </div>
            </div>

            {/* Next Event */}
            <div className="lg:col-span-4 glass-card flex flex-col">
              <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
                <Calendar size={16} className="text-amber-400" />
                Próximo Evento
              </h3>
              {(() => {
                const scheduledPlaylists = nowPlaying?.nowPlaying?.scheduledPlaylists || [];
                if (scheduledPlaylists.length === 0) {
                  return (
                    <div className="text-center py-4">
                      <Calendar size={32} className="text-gray-600 mx-auto mb-2" />
                      <p className="text-xs text-gray-500">Sin eventos programados</p>
                    </div>
                  );
                }
                const now = new Date();
                const dayNames = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
                const events = [];
                for (const pl of scheduledPlaylists) {
                  if (!pl.days || !pl.start_time) continue;
                  const [sh, sm] = pl.start_time.split(':').map(Number);
                  const startMins = sh * 60 + sm;
                  const curMins = now.getHours() * 60 + now.getMinutes();
                  for (let d = 0; d < 7; d++) {
                    const day = dayNames[(now.getDay() + d) % 7];
                    if (!pl.days.includes(day)) continue;
                    const minsUntil = d === 0 ? startMins - curMins : d * 1440 - curMins + startMins;
                    if (minsUntil <= 0) continue;
                    const h = Math.floor(minsUntil / 60);
                    const m = minsUntil % 60;
                    events.push({ name: pl.name, timeLabel: h > 0 ? `en ${h}h ${m}m` : `en ${m}m`, startTime: pl.start_time, minsUntil });
                    break;
                  }
                }
                events.sort((a, b) => a.minsUntil - b.minsUntil);
                if (events.length === 0) return (
                  <div className="text-center py-4">
                    <Calendar size={32} className="text-gray-600 mx-auto mb-2" />
                    <p className="text-xs text-gray-500">Sin eventos próximos</p>
                  </div>
                );
                return (
                  <div className="flex flex-col gap-2">
                    {events.map((ev, i) => (
                      <div key={i} className={`flex items-center gap-3 p-3 rounded-xl border ${i === 0 ? 'bg-amber-500/10 border-amber-500/20' : 'bg-white/[0.03] border-white/[0.06]'}`}>
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${i === 0 ? 'bg-amber-500/20' : 'bg-white/5'}`}>
                          <CalendarClock size={15} className={i === 0 ? 'text-amber-400' : 'text-gray-500'} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-bold text-white truncate">{ev.name}</div>
                          <div className={`text-xs font-semibold ${i === 0 ? 'text-amber-400' : 'text-gray-500'}`}>{ev.timeLabel} · {ev.startTime}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* OBS Live Badge with neon glow */}
        {!nowPlayingLoading && nowPlaying && nowPlaying.isOBS && (
          <div className="mb-8 animate-fade-in relative">
            {/* Badge LIVE fuera del contenedor principal */}
            <div className="absolute top-2 right-2 pill-glass pill-online flex-shrink-0 z-10 border-[5px] border-status-live/50">
              <div className="w-2.5 h-2.5 bg-status-live rounded-full animate-pulse shadow-lg shadow-status-live/50"></div>
              <span className="font-semibold">LIVE</span>
            </div>
            
            <div className="badge-live relative overflow-hidden">
              {/* Animated background gradient */}
              <div className="absolute inset-0 bg-gradient-to-r from-status-live/5 via-status-live/10 to-status-live/5 animate-pulse"></div>

              <div className="relative flex items-center gap-4 pr-4 sm:pr-24">
                <div className="w-16 h-16 rounded-xl bg-status-live/20 backdrop-blur-sm flex items-center justify-center flex-shrink-0 border border-status-live/30">
                  <Radio className="text-status-live animate-pulse" size={32} />
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
                    <span className="inline-block w-3 h-3 bg-status-live rounded-full animate-pulse shadow-lg shadow-status-live/50"></span>
                    EN VIVO CON OBS
                  </h3>
                  <p className="text-gray-300 mb-2">
                    Canal: <span className="text-status-live font-semibold">{nowPlaying.channel.name}</span>
                  </p>
                  <p className="text-sm text-gray-400">
                    Estás transmitiendo en vivo desde OBS. La playlist se reanudará automáticamente cuando desconectes OBS.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Main Content Grid: 65% Charts + 35% Simulcast */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
          {/* Left Section: 65% - Playlist + Charts */}
          <div className="lg:col-span-8 space-y-6">
            {/* Compact Now Playing for Playlist */}
            {!nowPlayingLoading && nowPlaying && nowPlaying.isPlaylist && nowPlaying.nowPlaying && nowPlaying.nowPlaying.current && (
              <div className="glass-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-accent-purple/20 to-accent-indigo/20 backdrop-blur-sm flex items-center justify-center border border-accent-purple/30">
                    <Play className="text-accent-purple" size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">
                      Reproduciendo Ahora
                    </h3>
                    <p className="text-xs text-gray-400">
                      {nowPlaying.channel.name} • Loop #{nowPlaying.nowPlaying.loopCount + 1}
                    </p>
                  </div>
                </div>

                {/* Current Playing Compact */}
                <div className="glass-card mb-3 border border-accent-purple/20">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-accent-purple/30 to-accent-indigo/30 backdrop-blur-sm flex items-center justify-center flex-shrink-0 border border-accent-purple/40">
                      {nowPlaying.nowPlaying.current.is_spot ? (
                        <Megaphone className="text-accent-purple" size={20} />
                      ) : (
                        <Film className="text-accent-purple" size={20} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-white truncate">
                        {nowPlaying.nowPlaying.current.title}
                      </div>
                      <div className="text-xs text-gray-400">
                        {Math.floor(nowPlaying.nowPlaying.current.progress / 60)}:{String(Math.floor(nowPlaying.nowPlaying.current.progress % 60)).padStart(2, '0')} / {Math.floor(nowPlaying.nowPlaying.current.duration / 60)}:{String(Math.floor(nowPlaying.nowPlaying.current.duration % 60)).padStart(2, '0')}
                      </div>
                    </div>
                    <div className="text-lg font-bold bg-gradient-to-r from-accent-purple to-accent-indigo bg-clip-text text-transparent">
                      {nowPlaying.nowPlaying.current.progressPercent}%
                    </div>
                  </div>
                  {/* Mini Progress Bar */}
                  <div className="relative w-full h-1.5 bg-white/5 rounded-full overflow-hidden mt-2">
                    <div
                      className="h-1.5 bg-gradient-to-r from-accent-purple via-accent-indigo to-accent-purple rounded-full transition-all duration-1000"
                      style={{ width: `${nowPlaying.nowPlaying.current.progressPercent}%` }}
                    ></div>
                  </div>
                </div>

                {/* Upcoming Queue Compact */}
                <div>
                  <h4 className="text-xs font-semibold text-gray-400 mb-2 uppercase tracking-wider">
                    Próximos en cola
                  </h4>
                  <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.1) transparent' }}>
                    {(() => {
                      // Compute next scheduled playlist using browser local time (timezone-correct)
                      const scheduledPlaylists = nowPlaying.nowPlaying.scheduledPlaylists || [];
                      const upcoming = nowPlaying.nowPlaying.upcoming;
                      const current = nowPlaying.nowPlaying.current;
                      const remainingCurrent = (current.duration || 0) - (current.progress || 0);

                      let nextSched = null;
                      if (scheduledPlaylists.length > 0) {
                        const now = new Date();
                        const dayNames = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];
                        const curMins = now.getHours() * 60 + now.getMinutes();
                        let minSecs = Infinity;
                        for (const pl of scheduledPlaylists) {
                          if (!pl.days || !pl.start_time) continue;
                          const [sh, sm] = pl.start_time.split(':').map(Number);
                          const startMins = sh * 60 + sm;
                          for (let d = 0; d < 7; d++) {
                            const day = dayNames[(now.getDay() + d) % 7];
                            if (!pl.days.includes(day)) continue;
                            const minsUntil = d === 0 ? startMins - curMins : d * 1440 - curMins + startMins;
                            if (minsUntil <= 0) continue;
                            const secsUntil = minsUntil * 60;
                            if (secsUntil < minSecs) {
                              minSecs = secsUntil;
                              const secsAfterCurrent = secsUntil - remainingCurrent;
                              let pos = secsAfterCurrent <= 0 ? 0 : -1;
                              if (pos < 0) {
                                let acc = 0;
                                for (let i = 0; i < upcoming.length; i++) {
                                  acc += upcoming[i].duration || 0;
                                  if (acc >= secsAfterCurrent) { pos = i + 1; break; }
                                }
                              }
                              if (pos >= 0) {
                                const h = Math.floor(secsUntil / 3600);
                                const m = Math.floor((secsUntil % 3600) / 60);
                                nextSched = { playlist_name: pl.name, seconds_until: secsUntil, transition_at_position: pos, timeLabel: h > 0 ? `en ${h}h ${m}m` : `en ${m}m` };
                              }
                            }
                            break;
                          }
                        }
                      }

                      const items = [];
                      upcoming.forEach((item, index) => {
                        if (nextSched && index === nextSched.transition_at_position) {
                          items.push(
                            <div key="sched-divider" className="flex items-center gap-2 py-1">
                              <div className="h-px flex-1 bg-amber-500/40"></div>
                              <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/30">
                                <CalendarClock size={10} className="text-amber-400 flex-shrink-0" />
                                <span className="text-xs text-amber-400 font-semibold whitespace-nowrap">
                                  {nextSched.playlist_name} · {nextSched.timeLabel}
                                </span>
                              </div>
                              <div className="h-px flex-1 bg-amber-500/40"></div>
                            </div>
                          );
                        }
                        items.push(
                          <div
                            key={index}
                            className={`flex items-center gap-2 p-2 rounded-lg hover:bg-white/10 transition-all ${
                              item.is_avance
                                ? 'bg-sky-500/10 border border-sky-500/20'
                                : item.is_spot
                                  ? 'bg-accent-purple/10 border border-accent-purple/20'
                                  : 'bg-white/5'
                            }`}
                          >
                            <div className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                              item.is_avance ? 'bg-sky-500/40 text-sky-400' : item.is_spot ? 'bg-accent-purple/40 text-accent-purple' : 'bg-white/10 text-gray-400'
                            }`}>
                              {index + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className={`text-xs font-medium truncate ${
                                item.is_avance ? 'text-sky-400' : item.is_spot ? 'text-accent-purple' : 'text-white'
                              }`}>
                                {item.is_avance && <Tv2 size={11} className="mr-1 flex-shrink-0 inline" />}
                                {item.is_spot && !item.is_avance && <span className="mr-1">★</span>}
                                {item.title}
                              </div>
                            </div>
                            <div className="text-xs text-gray-400 flex-shrink-0">
                              {Math.floor(item.duration / 60)}:{String(item.duration % 60).padStart(2, '0')}
                            </div>
                          </div>
                        );
                      });
                      return items;
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* Charts */}
            <div className="grid grid-cols-1 gap-6">
              {/* Viewers Chart */}
              <div className="glass-card">
                <h3 className="text-xl font-semibold mb-6 text-white flex items-center gap-2">
                  <Eye size={20} className="text-accent-indigo" />
                  Espectadores en la Sesión
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={sessionStats.chartData}>
                    <defs>
                      <linearGradient id="colorViewersGlow" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#818cf8" stopOpacity={0.4}/>
                        <stop offset="50%" stopColor="#6366f1" stopOpacity={0.2}/>
                        <stop offset="95%" stopColor="#4f46e5" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                    <XAxis dataKey="time" stroke={axisStroke} style={{ fontSize: '12px' }} />
                    <YAxis stroke={axisStroke} style={{ fontSize: '12px' }} />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      labelStyle={tooltipLabelStyle}
                      itemStyle={{ color: '#818cf8' }}
                    />
                    <Area
                      type="monotone"
                      dataKey="viewers"
                      stroke="#818cf8"
                      strokeWidth={3}
                      fillOpacity={1}
                      fill="url(#colorViewersGlow)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Bandwidth Chart */}
              <div className="glass-card">
                <h3 className="text-xl font-semibold mb-6 text-white flex items-center gap-2">
                  <Activity size={20} className="text-accent-emerald" />
                  Ancho de Banda en la Sesión
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={sessionStats.chartData}>
                    <defs>
                      <linearGradient id="colorBandwidthGlow" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#34d399" stopOpacity={0.4}/>
                        <stop offset="50%" stopColor="#10b981" stopOpacity={0.2}/>
                        <stop offset="95%" stopColor="#059669" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} />
                    <XAxis dataKey="time" stroke={axisStroke} style={{ fontSize: '12px' }} />
                    <YAxis stroke={axisStroke} style={{ fontSize: '12px' }} />
                    <Tooltip
                      contentStyle={{ ...tooltipStyle, border: isDark ? '1px solid rgba(52,211,153,0.3)' : '1px solid rgba(16,185,129,0.2)' }}
                      labelStyle={tooltipLabelStyle}
                      itemStyle={{ color: '#34d399' }}
                    />
                    <Area
                      type="monotone"
                      dataKey="bandwidth"
                      stroke="#34d399"
                      strokeWidth={3}
                      fillOpacity={1}
                      fill="url(#colorBandwidthGlow)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Right Section: 35% - Simulcast Panel */}
          <div className="lg:col-span-4">
            <div className="glass-card sticky top-4">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white">
                  SIMULCAST DISTRIBUTION
                </h3>
                <button
                  onClick={() => window.location.href = '/simulcast'}
                  className="text-sm font-semibold text-accent-purple hover:text-accent-indigo transition-colors uppercase tracking-wider"
                >
                  MANAGE
                </button>
              </div>

              {simulcastLoading ? (
                <div className="flex justify-center py-8">
                  <div className="w-8 h-8 border-2 border-accent-purple/30 border-t-accent-purple rounded-full animate-spin"></div>
                </div>
              ) : !Array.isArray(simulcastPlatforms) || simulcastPlatforms.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 mx-auto mb-4 rounded-xl bg-gradient-to-br from-accent-purple/20 to-accent-indigo/20 backdrop-blur-sm flex items-center justify-center border border-accent-purple/30">
                    <Radio size={40} className="text-accent-purple opacity-50" />
                  </div>
                  <p className="text-gray-400 text-sm mb-2 font-medium">No hay plataformas configuradas</p>
                  <p className="text-gray-500 text-xs mb-6">
                    Crea y configura plataformas de simulcast para poder activarlas desde aquí
                  </p>
                  <button
                    onClick={() => window.location.href = '/simulcast'}
                    className="btn-primary-gradient"
                  >
                    Crear Plataforma
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {Array.isArray(simulcastPlatforms) && simulcastPlatforms.map((platform) => {
                    const platformIcons = {
                      youtube: <svg width="24" height="24" viewBox="0 0 24 24" fill="white"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>,
                      twitch: <svg width="24" height="24" viewBox="0 0 24 24" fill="white"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/></svg>,
                      facebook: <svg width="24" height="24" viewBox="0 0 24 24" fill="white"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
                      custom: '☁️'
                    };

                    const platformColors = {
                      youtube: 'from-red-600 to-red-700',
                      twitch: 'from-purple-600 to-purple-700',
                      facebook: 'from-blue-600 to-blue-700',
                      periscope: 'from-sky-500 to-sky-700',
                      custom: 'from-gray-600 to-gray-700'
                    };

                    const statusColor = platform.enabled
                      ? 'text-accent-emerald'
                      : 'text-gray-500';

                    return (
                      <div
                        key={platform.id}
                        className="glass-card group hover:border-accent-purple/30 transition-all"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-2xl flex-shrink-0 bg-gradient-to-br ${platformColors[platform.platform] || platformColors.custom} shadow-lg`}>
                            {platformIcons[platform.platform] || platformIcons.custom}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-white">
                              {platform.channel_name || 'Simulcast'}
                            </div>
                            <div className="text-xs text-gray-400">
                              {platform.platform === 'youtube' && 'YouTube Live'}
                              {platform.platform === 'twitch' && 'Twitch TV'}
                              {platform.platform === 'facebook' && 'Facebook Live'}
                              {platform.platform === 'custom' && 'Custom CDN'}
                              {platform.platform === 'periscope' && 'Periscope'}
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <button
                              onClick={() => toggleSimulcast(platform.id, platform.enabled)}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                platform.enabled
                                  ? 'bg-accent-emerald'
                                  : 'bg-gray-600'
                              }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  platform.enabled ? 'translate-x-6' : 'translate-x-1'
                                }`}
                              />
                            </button>
                            <span className={`text-xs font-semibold ${statusColor} uppercase`}>
                              {platform.enabled ? 'ACTIVE' : 'OFFLINE'}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Quick Stats */}
              <div className="mt-6 pt-6 border-t border-white/10">
                <div className="text-xs font-semibold text-gray-400 mb-3 uppercase tracking-wider">
                  INGEST PERFORMANCE
                </div>
                {simulcastPlatforms.length === 0 ? (
                  <p className="text-xs text-gray-500 italic">Sin plataformas configuradas</p>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-gray-400 mb-1">Plataformas</div>
                      <div className="text-2xl font-bold text-white">
                        {simulcastPlatforms.filter(p => p.enabled).length}
                        <span className="text-sm font-normal text-gray-400">/{simulcastPlatforms.length}</span>
                      </div>
                      <div className="text-xs text-gray-500">activas</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-400 mb-1">Formato</div>
                      <div className="text-lg font-bold text-white">
                        {simulcastPlatforms.some(p => p.enabled) ? 'H.264' : '—'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {simulcastPlatforms.some(p => p.enabled) ? 'RTMP / AAC' : 'sin distribución'}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Active Channels List with glassmorphism */}
        <div className="glass-card">
          <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
            <h3 className="text-xl font-semibold text-white flex items-center gap-2">
              <Radio size={20} className="text-accent-purple" />
              Canales Recientes
            </h3>
            <div className="flex gap-2">
              {isStreaming && (
                <button
                  className="btn-danger"
                  onClick={resetSessionStats}
                >
                  Reiniciar Estadísticas
                </button>
              )}
              <button
                className="btn-primary-gradient"
                onClick={() => window.location.href = '/channels'}
              >
                Ver Todos
              </button>
            </div>
          </div>

          {channels.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 mx-auto mb-4 rounded-xl bg-gradient-to-br from-accent-purple/20 to-accent-indigo/20 backdrop-blur-sm flex items-center justify-center border border-accent-purple/30">
                <Radio size={40} className="text-accent-purple opacity-50" />
              </div>
              <p className="text-gray-400 mb-4">No hay canales creados aún</p>
              <button
                className="btn-primary-gradient"
                onClick={() => window.location.href = '/channels'}
              >
                Crear Primer Canal
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-400 uppercase tracking-wider">
                      Canal
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-400 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-400 uppercase tracking-wider">
                      Espectadores
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-400 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-400 uppercase tracking-wider">
                      Creado
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {channels.map((channel) => (
                    <tr
                      key={channel.id}
                      className="border-b border-white/5 hover:bg-white/5 transition-all duration-300 group"
                    >
                      <td className="px-6 py-4">
                        <div className="font-semibold text-white group-hover:text-accent-purple transition-colors">
                          {channel.name}
                        </div>
                        <div className="text-sm text-gray-400">
                          {channel.description || 'Sin descripción'}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {channel.stream_key && channelOnlineStatus[channel.stream_key] === true ? (
                          <span className="pill-glass pill-online">
                            <Wifi size={12} />
                            <span>Online</span>
                          </span>
                        ) : channel.stream_key && channelOnlineStatus[channel.stream_key] === false ? (
                          <span className="pill-glass pill-offline">
                            <WifiOff size={12} />
                            <span>Offline</span>
                          </span>
                        ) : (
                          <span className="pill-glass" style={{ opacity: 0.5 }}>
                            <Wifi size={12} />
                            <span>—</span>
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Users size={16} className="text-gray-400" />
                          <span className="text-white font-medium">
                            {channel.stats?.viewers || 0}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-accent-indigo/20 text-accent-indigo border border-accent-indigo/30 backdrop-blur-sm">
                          {channel.type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-400">
                        {new Date(channel.created_at).toLocaleDateString('es-ES')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Dashboard;
